import { httpClient } from './http';

export interface TableBill {
  id: string;
  restaurantId: string;
  tableId: string;
  tableNumber: number;
  waiterId?: string;
  waiterName?: string;
  status: 'open' | 'closed' | 'paid';
  totalAmount: number;
  openedAt: string;
  closedAt?: string;
  paidAt?: string;
  paymentMethod?: string;
  notes?: string;
  orders: any[];
  ordersCount: number;
}

export interface TableBillStats {
  totalBills: number;
  openBills: number;
  closedBills: number;
  paidBills: number;
  totalRevenue: number;
  averageBillAmount: number;
}

export const tableBillsService = {
  // Normalizador de respuesta del backend a nuestro modelo tipado
  _normalize: (raw: any): TableBill => {
    const toNum = (v: any) => {
      const n = typeof v === 'string' ? parseFloat(v) : v;
      return Number.isFinite(n) ? (n as number) : 0;
    };
    return {
      id: raw?.id,
      restaurantId: raw?.restaurant_id ?? raw?.restaurantId ?? '',
      tableId: raw?.table_id ?? raw?.tableId ?? '',
      tableNumber: raw?.table_number ?? raw?.tableNumber ?? 0,
      waiterId: raw?.waiter_id ?? raw?.waiterId ?? undefined,
      waiterName: raw?.waiterName ?? undefined,
      status: (raw?.status as any) ?? 'open',
      totalAmount: toNum(raw?.total_amount ?? raw?.totalAmount),
      openedAt: raw?.opened_at ?? raw?.openedAt ?? '',
      closedAt: raw?.closed_at ?? raw?.closedAt ?? undefined,
      paidAt: raw?.paid_at ?? raw?.paidAt ?? undefined,
      paymentMethod: raw?.payment_method ?? raw?.paymentMethod ?? undefined,
      notes: raw?.notes ?? undefined,
      orders: Array.isArray(raw?.orders) ? raw.orders : [],
      ordersCount: typeof raw?.ordersCount === 'number'
        ? raw.ordersCount
        : (Array.isArray(raw?.orders) ? raw.orders.length : 0),
    };
  },
  // Crear cuenta para una mesa
  createTableBill: async (restaurantId: string, data: {
    tableId: string;
    tableNumber: number;
    waiterId?: string;
    notes?: string;
  }): Promise<TableBill> => {
    const response = await httpClient.post(`/table-bills/restaurants/${restaurantId}/bills`, data, 'authenticated') as any;
    return tableBillsService._normalize(response.data.data);
  },

  // Obtener cuenta por ID
  getTableBillById: async (restaurantId: string, billId: string): Promise<TableBill> => {
    const response = await httpClient.get(`/table-bills/restaurants/${restaurantId}/bills/${billId}`, undefined, 'authenticated') as any;
    return tableBillsService._normalize(response.data);
  },

  // Obtener cuentas de un restaurante
  getTableBillsByRestaurant: async (restaurantId: string, filters?: {
    status?: string;
    tableNumber?: number;
    waiterId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    size?: number;
  }): Promise<{ data: TableBill[]; pagination: any }> => {
    // Convertir números a strings para los parámetros de consulta
    const queryParams = filters ? {
      status: filters.status,
      tableNumber: filters.tableNumber?.toString(),
      waiterId: filters.waiterId,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      page: filters.page?.toString(),
      size: filters.size?.toString()
    } : undefined;
    
    const response = await httpClient.get(`/table-bills/restaurants/${restaurantId}/bills`, queryParams, 'authenticated') as any;
    const list = Array.isArray(response.data) ? response.data.map(tableBillsService._normalize) : [];
    return {
      data: list,
      pagination: response.data.pagination
    };
  },

  // Obtener cuenta abierta de una mesa
  getOpenTableBill: async (restaurantId: string, tableId: string): Promise<TableBill | null> => {
    try {
      const response = await httpClient.get(`/table-bills/restaurants/${restaurantId}/tables/${tableId}/bills/open`, undefined, 'authenticated') as any;
      return response.data ? tableBillsService._normalize(response.data) : null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  // Cerrar cuenta
  closeTableBill: async (restaurantId: string, billId: string): Promise<TableBill> => {
    const response = await httpClient.put(`/table-bills/restaurants/${restaurantId}/bills/${billId}/close`, undefined, 'authenticated') as any;
    return tableBillsService._normalize(response.data.data);
  },

  // Pagar cuenta
  payTableBill: async (restaurantId: string, billId: string, data: {
    paymentMethod: 'cash' | 'card' | 'online';
    notes?: string;
  }): Promise<TableBill> => {
    const response = await httpClient.put(`/table-bills/restaurants/${restaurantId}/bills/${billId}/pay`, data, 'authenticated') as any;
    return tableBillsService._normalize(response.data.data);
  },

  // Obtener estadísticas de cuentas
  getTableBillStats: async (restaurantId: string, filters?: {
    dateFrom?: string;
    dateTo?: string;
  }): Promise<TableBillStats> => {
    const response = await httpClient.get(`/table-bills/restaurants/${restaurantId}/bills/stats`, filters, 'authenticated') as any;
    return response.data.data;
  }
};
